import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Drawer, Stack, Button, CircularProgress,
  Alert, Chip, Switch, FormControlLabel, Divider, IconButton,
  Tooltip, TextField, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../../components/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAdminsApi, createAdminApi, updateAdminApi, deleteAdminApi,
} from '../../services/api';

const TABS = [
  { key: 'users',      label: 'Users' },
  { key: 'categories', label: 'Channels' },
  { key: 'shows',      label: 'Shows' },
  { key: 'userLogs',   label: 'User Logs' },
  { key: 'admins',     label: 'Admins' },
];

const DEFAULT_ACCESS = {
  users:      { read: false, write: false },
  categories: { read: false, write: false },
  shows:      { read: false, write: false },
  userLogs:   { read: false, write: false },
  admins:     { read: false, write: false },
};

const EMPTY_FORM = {
  name: '', email: '', password: '', userType: 'admin',
  adminAccess: DEFAULT_ACCESS,
};

const fmtDate = (v) => {
  if (!v) return 'N/A';
  const d = new Date(v);
  if (isNaN(d)) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(d);
};

const mergeAccess = (saved) => {
  const base = JSON.parse(JSON.stringify(DEFAULT_ACCESS));
  if (!saved) return base;
  TABS.forEach(({ key }) => {
    if (saved[key]) {
      base[key].read = !!saved[key].read;
      base[key].write = !!saved[key].write;
    }
  });
  return base;
};

function AccessSummary({ adminAccess }) {
  const access = mergeAccess(adminAccess);
  const chips = TABS.filter(({ key }) => access[key].read || access[key].write);
  if (!chips.length) return <Typography variant="caption" color="text.disabled">No access</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.5 }}>
      {chips.map(({ key, label }) => {
        const r = access[key].read;
        const w = access[key].write;
        return (
          <Chip
            key={key}
            label={`${label}: ${r && w ? 'R/W' : r ? 'Read' : 'Write'}`}
            size="small"
            variant="outlined"
            color={w ? 'primary' : 'default'}
            sx={{ fontSize: 11 }}
          />
        );
      })}
    </Stack>
  );
}

function AccessToggles({ access, onChange }) {
  const toggle = (tab, type) => {
    onChange({ ...access, [tab]: { ...access[tab], [type]: !access[tab][type] } });
  };
  return (
    <Stack spacing={1.5}>
      {TABS.map(({ key, label }) => (
        <Box key={key} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e8eaf0', bgcolor: '#fafafa' }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{label}</Typography>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={<Switch size="small" checked={access[key].read} onChange={() => toggle(key, 'read')} color="primary" />}
              label={<Typography variant="caption">Read</Typography>}
            />
            <FormControlLabel
              control={<Switch size="small" checked={access[key].write} onChange={() => toggle(key, 'write')} color="primary" />}
              label={<Typography variant="caption">Write</Typography>}
            />
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export default function Admins() {
  const { user: authUser } = useAuth();
  const canWrite = !authUser?.adminAccess?.admins || authUser.adminAccess.admins.write === true;

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);

  // Drawer (edit)
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [initialForm, setInitialForm] = useState({});
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerError, setDrawerError] = useState('');

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isDirty = JSON.stringify(editForm) !== JSON.stringify(initialForm);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminsApi();
      setAdmins(data.map((a) => ({ ...a, id: a._id })));
    } catch {
      // silently fail — grid stays empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  // ── Edit drawer ──────────────────────────────────────────────────────────

  const openDrawer = (admin) => {
    const form = {
      name: admin.name,
      email: admin.email,
      userType: admin.userType,
      password: '',
      adminAccess: mergeAccess(admin.adminAccess),
    };
    setSelected(admin);
    setEditForm(form);
    setInitialForm(form);
    setDrawerError('');
  };

  const closeDrawer = () => { setSelected(null); setDrawerError(''); };

  const handleEditSave = async () => {
    setDrawerSaving(true);
    setDrawerError('');
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      const { data } = await updateAdminApi(selected._id, payload);
      setAdmins((prev) => prev.map((a) => a._id === data._id ? { ...data, id: data._id } : a));
      const updated = { ...editForm, password: '' };
      setEditForm(updated);
      setInitialForm(updated);
    } catch (err) {
      setDrawerError(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setDrawerSaving(false);
    }
  };

  // ── Add dialog ───────────────────────────────────────────────────────────

  const openAdd = () => { setAddForm(EMPTY_FORM); setAddError(''); setAddOpen(true); };
  const closeAdd = () => { setAddOpen(false); setAddError(''); };

  const handleAddSave = async () => {
    setAddSaving(true);
    setAddError('');
    try {
      const { data } = await createAdminApi(addForm);
      setAdmins((prev) => [{ ...data, id: data._id }, ...prev]);
      closeAdd();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to create admin user.');
    } finally {
      setAddSaving(false);
    }
  };

  // ── Delete dialog ────────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteAdminApi(deleteTarget._id);
      setAdmins((prev) => prev.filter((a) => a._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch {
      // keep dialog open so user can retry
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Columns ──────────────────────────────────────────────────────────────

  const COLUMNS = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    {
      field: 'userType', headerName: 'Role', width: 130,
      renderCell: (p) => (
        <Chip
          label={p.value === 'superadmin' ? 'Super Admin' : 'Admin'}
          size="small"
          color={p.value === 'superadmin' ? 'warning' : 'default'}
        />
      ),
    },
    {
      field: 'adminAccess', headerName: 'Access', flex: 2, minWidth: 300, sortable: false,
      renderCell: (p) => <AccessSummary adminAccess={p.value} />,
    },
    {
      field: 'createdAt', headerName: 'Added', width: 170,
      renderCell: (p) => fmtDate(p.value),
    },
    ...(canWrite ? [{
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: (p) => (
        <Stack direction="row">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDrawer(p.row); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(p.row); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    }] : []),
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Admins"
        subtitle="Manage admin accounts and their tab access permissions"
        action={canWrite
          ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
              sx={{ bgcolor: '#fec958', color: '#000', '&:hover': { bgcolor: '#f5bc40' } }}>
              Add Admin
            </Button>
          )
          : <Chip label="Read only" size="small" variant="outlined" />
        }
      />

      <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e8eaf0', overflow: 'hidden' }}>
        <DataGrid
          rows={admins}
          columns={COLUMNS}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 'none', '& .MuiDataGrid-columnHeaders': { bgcolor: '#f8f9fc' } }}
          onRowClick={(p) => { if (!canWrite) return; openDrawer(p.row); }}
        />
      </Box>

      {/* ── Edit Drawer ── */}
      <Drawer anchor="right" open={!!selected} onClose={closeDrawer}
        PaperProps={{ sx: { width: 400, p: 3 } }}>
        {selected && (
          <Stack spacing={2.5} sx={{ height: '100%', width: "100%", p: 3 }}>
            <Box style={{display: "flex", width: "100%"}} direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight={700}>Edit Admin</Typography>
              <IconButton onClick={closeDrawer}><CloseIcon /></IconButton>
            </Box>

            <Divider />

            <TextField label="Name" size="small" fullWidth value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />

            <TextField label="Email" size="small" fullWidth value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />

            <TextField label="New Password" size="small" fullWidth type="password"
              placeholder="Leave blank to keep current"
              value={editForm.password}
              onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} />

            <TextField label="Role" size="small" fullWidth select value={editForm.userType}
              onChange={(e) => setEditForm((f) => ({ ...f, userType: e.target.value }))}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="superadmin">Super Admin</MenuItem>
            </TextField>

            <Divider />

            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">TAB ACCESS</Typography>

            <AccessToggles
              access={editForm.adminAccess}
              onChange={(a) => setEditForm((f) => ({ ...f, adminAccess: a }))}
            />

            {drawerError && <Alert severity="error" onClose={() => setDrawerError('')}>{drawerError}</Alert>}

            <Box sx={{ flex: 1 }} />

            <Button variant="contained" fullWidth disabled={!isDirty || drawerSaving} onClick={handleEditSave}
              sx={{ bgcolor: '#fec958', color: '#000', '&:hover': { bgcolor: '#f5bc40' } }}>
              {drawerSaving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </Drawer>

      {/* ── Add Dialog ── */}
      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Add Admin User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" size="small" fullWidth value={addForm.name}
              onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Email" size="small" fullWidth value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} />
            <TextField label="Password" size="small" fullWidth type="password" value={addForm.password}
              onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))} />
            <TextField label="Role" size="small" fullWidth select value={addForm.userType}
              onChange={(e) => setAddForm((f) => ({ ...f, userType: e.target.value }))}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="superadmin">Super Admin</MenuItem>
            </TextField>

            <Divider />
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">TAB ACCESS</Typography>

            <AccessToggles
              access={addForm.adminAccess}
              onChange={(a) => setAddForm((f) => ({ ...f, adminAccess: a }))}
            />

            {addError && <Alert severity="error" sx={{ mt: 1 }}>{addError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAdd}>Cancel</Button>
          <Button variant="contained" disabled={addSaving} onClick={handleAddSave}
            sx={{ bgcolor: '#fec958', color: '#000', '&:hover': { bgcolor: '#f5bc40' } }}>
            {addSaving ? <CircularProgress size={18} /> : 'Create Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle fontWeight={700}>Delete Admin?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={deleteLoading} onClick={handleDelete}>
            {deleteLoading ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
